import type { Field } from '@evidia/components';
import { RULE_STUDENT_FIRST_NAME, RULE_STUDENT_LAST_NAME, createSchemaFieldRule } from '@evidia/components';
import {
	APPLICATION_FORM_COURSE_VALIDATORS,
	APPLICATION_FORM_VALIDATORS,
	CONTACT_MESSAGE_VALIDATORS,
	COUNTRIES,
	DRIVING_LESSONS_RECORD_VALIDATORS,
	ExamType,
	INTERPRETER_ADDRESS_VALIDATORS,
	INTERPRETER_INFO_VALIDATORS,
	PAYMENT_ITEM_VALIDATORS,
	PRAGUE_OFFICES,
	SCHOOL_OPERATOR_VALIDATORS,
	STUDENT_CODE_VALIDATORS,
	STUDENT_STUDY_INFO_VALIDATORS,
	USER_VALIDATORS,
} from '@evidia/shared';
import dayjs from 'dayjs';
import { z } from 'zod';
import { STUDENT_GROUP_LIST } from './const';

// user
export const RULE_USER_EMAIL = createSchemaFieldRule(z.object({ email: USER_VALIDATORS.email.in }));

export const RULE_USER_PHONE = createSchemaFieldRule(z.object({ phone: USER_VALIDATORS.phone.in }));

export const RULE_USER_PASSWORD = createSchemaFieldRule(z.object({ password: USER_VALIDATORS.password.in }));

export const RULE_USER_FIRST_NAME = createSchemaFieldRule(z.object({ firstName: USER_VALIDATORS.firstName.in }));

export const RULE_USER_LAST_NAME = createSchemaFieldRule(z.object({ lastName: USER_VALIDATORS.lastName.in }));

export const STUDENT_STUDY_INFO_NOTE = createSchemaFieldRule(z.object({ note: STUDENT_STUDY_INFO_VALIDATORS.note.in }));

// contact message
export const RULE_CONTACT_MESSAGE_EMAIL = createSchemaFieldRule(
	z.object({ email: CONTACT_MESSAGE_VALIDATORS.email.in }),
);

export const RULE_CONTACT_MESSAGE_MESSAGE = createSchemaFieldRule(
	z.object({ message: CONTACT_MESSAGE_VALIDATORS.message.in }),
);

// paymentitem
export const RULE_PAYMENT_ITEM_PRICE = createSchemaFieldRule(z.object({ price: PAYMENT_ITEM_VALIDATORS.price.in }));

// driving lesson record

export const RULE_DRIVING_LESSON_START_TIME = (fieldName: string) =>
	createSchemaFieldRule(z.object({ [fieldName]: DRIVING_LESSONS_RECORD_VALIDATORS.startTime.in }));

export const RULE_DRIVING_LESSON_TACHOMETER_BEFORE = createSchemaFieldRule(
	z.object({ tachometerBefore: DRIVING_LESSONS_RECORD_VALIDATORS.tachometerBefore.in }),
);

export const RULE_DRIVING_LESSON_TACHOMETER_AFTER = createSchemaFieldRule(
	z.object({ tachometerAfter: DRIVING_LESSONS_RECORD_VALIDATORS.tachometerAfter.in }),
);

// application form
export const RULE_APPLICATION_FORM_NAME = createSchemaFieldRule(
	z.object({ name: APPLICATION_FORM_VALIDATORS.name.in }),
);
export const RULE_APPLICATION_ORIGINS = createSchemaFieldRule(
	z.object({ origins: APPLICATION_FORM_VALIDATORS.origins.in }),
);

// application form course
export const RULE_APPLICATION_FORM_COURSE_NAME = createSchemaFieldRule(
	z.object({ name: APPLICATION_FORM_COURSE_VALIDATORS.name.in }),
);
export const RULE_APPLICATION_FORM_COURSE_DESCRIPTION = createSchemaFieldRule(
	z.object({ description: APPLICATION_FORM_COURSE_VALIDATORS.description.in }),
);
export const RULE_APPLICATION_FORM_COURSE_PRICE = createSchemaFieldRule(
	z.object({ price: APPLICATION_FORM_COURSE_VALIDATORS.price.in }),
);

// school operator
export const RULE_SCHOOL_OPERATOR_NAME = createSchemaFieldRule(z.object({ name: SCHOOL_OPERATOR_VALIDATORS.name.in }));

export const RULE_SCHOOL_OPERATOR_COMPANY_NUMBER = createSchemaFieldRule(
	z.object({ companyNumber: SCHOOL_OPERATOR_VALIDATORS.companyNumber.in }),
);

export const RULE_SCHOOL_OPERATOR_STREET = createSchemaFieldRule(
	z.object({ street: SCHOOL_OPERATOR_VALIDATORS.street.in }),
);

export const RULE_SCHOOL_OPERATOR_STREET_CODE = createSchemaFieldRule(
	z.object({ streetCode: SCHOOL_OPERATOR_VALIDATORS.streetCode.in }),
);

export const RULE_SCHOOL_OPERATOR_CITY = createSchemaFieldRule(z.object({ city: SCHOOL_OPERATOR_VALIDATORS.city.in }));

export const RULE_SCHOOL_OPERATOR_POST_CODE = createSchemaFieldRule(
	z.object({ postCode: SCHOOL_OPERATOR_VALIDATORS.postCode.in }),
);

// student code
export const RULE_STUDENT_CODE_SECRET_CODE = createSchemaFieldRule(
	z.object({ secretCode: STUDENT_CODE_VALIDATORS.secretCode.in }),
);

export const PASSWORD_FORMAT_VALIDATOR = {
	pattern: new RegExp('^(?=.*\\W)(?=.{7,})'),
	message: 'Neplatý formát. Heslo musí obsahovat minimálně 6 písmen a spec. znak!',
};

// interpreter
export const RULE_INTERPRETER_FIRST_NAME = createSchemaFieldRule(
	z.object({ firstName: INTERPRETER_INFO_VALIDATORS.firstName.in }),
);

export const RULE_INTERPRETER_LAST_NAME = createSchemaFieldRule(
	z.object({ lastName: INTERPRETER_INFO_VALIDATORS.lastName.in }),
);

export const RULE_INTERPRETER_DEGREE = createSchemaFieldRule(
	z.object({ degree: INTERPRETER_INFO_VALIDATORS.degree.in }),
);

export const RULE_INTERPRETER_BIRTH_DATE = createSchemaFieldRule(
	z.object({ birthDate: INTERPRETER_INFO_VALIDATORS.birthDate.in }),
);

export const RULE_INTERPRETER_PHONE = createSchemaFieldRule(z.object({ phone: INTERPRETER_INFO_VALIDATORS.phone.in }));

export const RULE_INTERPRETER_ADDRESS_STREET = createSchemaFieldRule(
	z.object({ street: INTERPRETER_ADDRESS_VALIDATORS.street.in }),
);

export const RULE_INTERPRETER_ADDRESS_STREET_CODE = createSchemaFieldRule(
	z.object({ streetCode: INTERPRETER_ADDRESS_VALIDATORS.streetCode.in }),
);

export const RULE_INTERPRETER_ADDRESS_CITY = createSchemaFieldRule(
	z.object({ city: INTERPRETER_ADDRESS_VALIDATORS.city.in }),
);

export const RULE_INTERPRETER_ADDRESS_POST_CODE = createSchemaFieldRule(
	z.object({ postCode: INTERPRETER_ADDRESS_VALIDATORS.postCode.in }),
);

export const USER_INFO: Field[] = [
	{
		key: 'firstName',
		label: 'Jméno',
		type: 'string',
		placeholder: 'Jan',
		required: true,
		rules: [RULE_STUDENT_FIRST_NAME],
	},
	{
		key: 'lastName',
		label: 'Příjmení',
		type: 'string',
		placeholder: 'Zaučil',
		required: true,
		rules: [RULE_STUDENT_LAST_NAME],
	},
];

export const SCHOOL_INFO: Field[] = [
	{
		key: 'name',
		label: 'Název autoškoly',
		type: 'string',
		placeholder: 'Autoškola Diane',
		required: true,
		rules: [{ required: true, message: 'Prosím zadejte název autoškoly!' }],
	},
	{
		key: 'number',
		label: 'Číslo autoškoly',
		type: 'string',
		placeholder: '270102',
		required: true,
		rules: [{ required: true, message: 'Prosím zadejte číslo autoškoly!' }],
	},
	{
		key: 'office',
		label: 'Přidělený úřad',
		type: 'string',
		placeholder: 'MeÚ Černošice',
		required: true,
		rules: [{ required: true, message: 'Prosím zadejte přidělený úřad!' }],
	},
];

export const SCHOOL_INFO_PRAGUE: Field[] = [
	{
		key: 'numberPrague',
		label: 'Číslo autoškoly pro Prahu (hlášenky)',
		type: 'string',
		placeholder: '452',
		required: false,
		showWhen: {
			office: (value: string) => PRAGUE_OFFICES.includes(value),
		},
	},
];

export const SCHOOL_ADDRES: Field[] = [
	{
		key: 'street',
		label: 'Ulice',
		type: 'string',
		placeholder: 'Pecháčkova',
		required: true,
		rules: [{ required: true, message: 'Prosím zadejte ulici!' }],
	},
	{
		key: 'streetCode',
		label: 'Číslo popisné',
		type: 'string',
		placeholder: '435',
		required: true,
		rules: [{ required: true, message: 'Prosím zadejte číslo popisné!' }],
	},
	{
		key: 'city',
		label: 'Město',
		type: 'string',
		placeholder: 'Praha',
		required: true,
		rules: [{ required: true, message: 'Prosím zadejte město!' }],
	},
	{
		key: 'postCode',
		label: 'PSČ',
		type: 'string',
		placeholder: '14100',
		required: true,
		rules: [{ required: true, message: 'Prosím zadejte PSČ!' }],
	},
];

export const CAR: Field[] = [
	{
		key: 'carType',
		label: 'Tovární značka vozidla',
		placeholder: 'Škoda Octavia',
		type: 'string',
		required: true,
		rules: [{ required: true, message: 'Prosím zadejte tovární značka vozidla!' }],
	},
	{
		key: 'regNumber',
		label: 'SPZ',
		type: 'string',
		placeholder: '4SX5658',
		required: true,
		normalize(value) {
			if (value.length > 8) {
				return value.substring(0, 8);
			}

			return value.toUpperCase();
		},
		rules: [
			{ required: true, message: 'Prosím zadejte SPZ!' },
			{ max: 8, message: 'Maximální délka SPZ je 8 znaků!' },
		],
	},
	{
		key: 'targetGroup',
		label: 'Pro skupiny',
		placeholder: 'Automobil, motocykly, nákladní atd.',
		rules: [{ required: true, message: 'Prosím zadajte skupiny!' }],
		type: 'select',
		required: true,
		mode: 'multiple',
		groups: true,
		values: STUDENT_GROUP_LIST,
	},
];

export const STUDENT_INFO: Field[] = [
	{
		key: 'firstName',
		label: 'Jméno',
		type: 'string',
		placeholder: 'Petr',
		required: true,
		rules: [RULE_STUDENT_FIRST_NAME],
	},
	{
		key: 'lastName',
		label: 'Příjmení',
		type: 'string',
		placeholder: 'Novák',
		required: true,
		rules: [RULE_STUDENT_LAST_NAME],
	},
	{
		key: 'degree',
		label: 'Titul',
		type: 'string',
		required: false,
		placeholder: 'Mgr.',
	},
	{
		key: 'birthDate',
		label: 'Datum narození',
		placeholder: '20.03.1998',
		type: 'day-picker',
		maxDate: dayjs().add(1, 'd').startOf('day'),
		required: true,
		rules: [{ required: true, message: 'Prosím zadejte datum narození!' }],
	},
	{
		key: 'birthPlace',
		label: 'Místo narození',
		type: 'string',
		placeholder: 'Praha',
		required: true,
		rules: [{ required: true, message: 'Prosím zadejte místo narození!' }],
	},
	{
		key: 'citizenship',
		label: 'Státní občanství',
		placeholder: 'Česká republika',
		required: true,
		type: 'select',
		values: COUNTRIES.map(country => ({ value: country, key: country })),
		rules: [{ required: true, message: 'Prosím zadejte datum státní občanství!' }],
	},
	// interpreter
	{
		key: 'docId',
		type: 'string',
		label: 'Doklad totožnosti',
		placeholder: '998001338',
		required: true,
		rules: [
			{
				required: true,
				message: 'Prosím zadejte platný doklad totožnosti!',
				// pattern: /^\d+$/,
			},
		],
	},
	{
		key: 'phone',
		type: 'string',
		label: 'Telefon',
		required: false,
		placeholder: '777235627',
		rules: [RULE_USER_PHONE],
	},
	{
		key: 'email',
		label: 'E-mail',
		required: false,
		type: 'string',
		rules: [
			{
				type: 'email',
				message: 'Neplatný e-mail!',
			},
		],
		placeholder: 'evidiaonline@gmail.com',
	},
];

export const STUDENT_INFO_SERVER: Field[] = [
	{
		key: 'registrationNumber',
		label: 'Evidenční číslo',
		placeholder: '23001',
		type: 'string',
		required: true,
		rules: [{ required: true, message: 'Prosím zadejte evidenční číslo!' }],
	},
];

export const NEW_EXAM_FIELDS: Field[] = [
	{
		key: 'date',
		type: 'day-picker',
		label: 'Datum žkoušky',
		placeholder: '23.2.2022',
		required: true,
		rules: [{ required: true, message: 'Prosím zadejte datum zkoušky!' }],
	},
	{
		key: 'place',
		label: 'Místo konání',
		required: true,
		type: 'string',
		placeholder: 'MěÚ Černošice',
		rules: [{ required: true, message: 'Prosím zadejte místo zkoušky!' }],
	},
	{
		key: 'type',
		label: 'Zkouška z',
		required: true,
		type: 'checkbox-group',
		values: [
			{ key: ExamType.PSP, value: 'Teorie (PSP)' },
			{ key: ExamType.PJ, value: 'Jízda (PJ)' },
			{ key: ExamType.OUV, value: 'Ovládání a údržba (OÚV)' },
			{ key: ExamType.CV, value: 'Cvičiště (CV)' },
		],
		rules: [{ required: true, message: 'Vyberte alespoň jeden druh zkoušky!' }],
	},
];

export const DATE_PICKER_FORMAT = 'D.M.YYYY';
